import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { stripHtml } from "../utils/html";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useIsMobile } from "../hooks/useIsMobile";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Parallax } from "react-scroll-parallax";
import { usePageTitle } from "../app/pageTitle";
import { productPath, pathFor } from "../app/paths";
import { API_BASE } from "../api/base";
import { parseApiDate } from "../lib/dates";

gsap.registerPlugin(ScrollTrigger);


interface RecommendedItem {
  type: "product" | "collection";
  slug: string;
  title: string;
  description: string;
  price_cents?: number;
  currency?: string;
  image_url: string | null;
}

interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body: string | null;
  image_url: string | null;
  created_at: string;
  recommended_items?: RecommendedItem[] | null;
}

/* ── Block types (matching dashboard editor) ── */
type Block =
  | { type: "paragraph"; text: string }
  | { type: "heading"; level: 2 | 3; text: string }
  | { type: "image"; url: string; caption: string }
  | { type: "youtube"; videoId: string }
  | { type: "quote"; text: string; attribution: string }
  | { type: "spacer" }
  | { type: "cta"; items: unknown[] };

function isBlockContent(body: string): boolean {
  try {
    const parsed = JSON.parse(body);
    return Array.isArray(parsed) && parsed.length > 0 && parsed[0].type;
  } catch {
    return false;
  }
}

/* ── Block Renderer ── */
function BlockRenderer({ blocks, cfg }: { blocks: Block[]; cfg: ReturnType<typeof useSiteConfig> }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.type) {
          case "paragraph":
            if (!block.text.trim()) return null;
            return (
              <p
                key={i}
                className="gs-block"
                style={{
                  fontSize: 16,
                  color: cfg.color_dark,
                  lineHeight: 1.8,
                  margin: "0 0 20px",
                  whiteSpace: "pre-line",
                }}
              >
                {block.text}
              </p>
            );

          case "heading":
            return block.level === 2 ? (
              <h2
                key={i}
                className="gs-block"
                style={{
                  fontFamily: cfg.font_heading,
                  fontSize: 28,
                  fontWeight: 400,
                  color: cfg.color_dark,
                  margin: "48px 0 16px",
                  lineHeight: 1.3,
                }}
              >
                {block.text}
              </h2>
            ) : (
              <h3
                key={i}
                className="gs-block"
                style={{
                  fontFamily: cfg.font_heading,
                  fontSize: 22,
                  fontWeight: 500,
                  color: cfg.color_dark,
                  margin: "36px 0 12px",
                  lineHeight: 1.3,
                }}
              >
                {block.text}
              </h3>
            );

          case "image":
            return (
              <figure key={i} className="gs-block" style={{ margin: "36px 0" }}>
                <img
                  src={resolveImageUrl(block.url)}
                  alt={block.caption || ""}
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
                {block.caption && (
                  <figcaption
                    style={{
                      fontSize: 13,
                      color: "rgba(28,75,59,0.86)",
                      textAlign: "center",
                      marginTop: 10,
                    }}
                  >
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );

          case "youtube":
            if (!block.videoId) return null;
            return (
              <div
                key={i}
                className="gs-block"
                style={{
                  position: "relative",
                  paddingBottom: "56.25%",
                  height: 0,
                  overflow: "hidden",
                  borderRadius: 8,
                  margin: "36px 0",
                  background: "#000",
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${block.videoId}`}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube video"
                />
              </div>
            );

          case "quote":
            return (
              <blockquote
                key={i}
                className="gs-block"
                style={{
                  borderLeft: `2px solid rgba(28,75,59,0.89)`,
                  margin: "36px 0",
                  padding: "16px 24px",
                  background: "transparent",
                }}
              >
                <p
                  style={{
                    fontStyle: "italic",
                    fontSize: 18,
                    color: cfg.color_dark,
                    lineHeight: 1.7,
                    margin: 0,
                    fontWeight: 400,
                  }}
                >
                  {block.text}
                </p>
                {block.attribution && (
                  <footer
                    style={{
                      fontSize: 13,
                      color: "rgba(28,75,59,0.86)",
                      marginTop: 10,
                      fontStyle: "normal",
                    }}
                  >
                    — {block.attribution}
                  </footer>
                )}
              </blockquote>
            );

          case "spacer":
            return <div key={i} style={{ height: 32 }} />;

          default:
            return null;
        }
      })}
    </>
  );
}

/* ── Legacy text renderer (for existing plain-text articles) ── */
function LegacyRenderer({ body, cfg }: { body: string; cfg: ReturnType<typeof useSiteConfig> }) {
  const paragraphs = body.split("\n").filter((l) => l.trim());

  return (
    <>
      {paragraphs.map((paragraph, i) => {
        const trimmed = paragraph.trim();

        // Detect section headers
        const isHeader =
          trimmed.length < 80 &&
          !trimmed.endsWith(".") &&
          !trimmed.endsWith(",") &&
          !trimmed.endsWith(":") &&
          !trimmed.startsWith(" ") &&
          i > 0 &&
          (trimmed.length < 60 || trimmed === trimmed.charAt(0).toUpperCase() + trimmed.slice(1));

        // Detect quote lines
        const isQuote =
          trimmed.startsWith('"') ||
          trimmed.startsWith("\u201E") ||
          trimmed.startsWith("\u00AB") ||
          trimmed.startsWith("\u201C");

        if (isQuote) {
          return (
            <blockquote
              key={i}
              className="gs-block"
              style={{
                borderLeft: `2px solid rgba(28,75,59,0.89)`,
                margin: "32px 0",
                padding: "16px 24px",
                background: "transparent",
                fontStyle: "italic",
                fontSize: 16,
                color: cfg.color_dark,
                lineHeight: 1.7,
              }}
            >
              {trimmed}
            </blockquote>
          );
        }

        if (isHeader && !trimmed.startsWith(" ") && trimmed.length > 3) {
          return (
            <h2
              key={i}
              className="gs-block"
              style={{
                fontFamily: cfg.font_heading,
                fontSize: 22,
                fontWeight: 500,
                color: cfg.color_dark,
                margin: "48px 0 16px",
                lineHeight: 1.3,
              }}
            >
              {trimmed}
            </h2>
          );
        }

        if (trimmed.startsWith(" ")) {
          return (
            <p
              key={i}
              className="gs-block"
              style={{
                fontSize: 16,
                color: cfg.color_dark,
                lineHeight: 1.8,
                margin: "4px 0",
                paddingLeft: 20,
                position: "relative",
              }}
            >
              <span style={{ position: "absolute", left: 0, color: "rgba(28,75,59,0.86)" }}>&bull;</span>
              {trimmed}
            </p>
          );
        }

        return (
          <p key={i} className="gs-block" style={{ fontSize: 16, color: cfg.color_dark, lineHeight: 1.8, margin: "0 0 16px" }}>
            {trimmed}
          </p>
        );
      })}
    </>
  );
}

function formatDate(iso: string, locale: string) {
  try {
    const d = parseApiDate(iso);
    return d.toLocaleDateString(locale === "en" ? "en-GB" : `${locale}-${locale.toUpperCase()}`, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

/* ── Main Component ── */
export function BlogArticle() {
  const { locale, slug } = useParams();
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const isMobile = useIsMobile();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Titolo pagina = titolo articolo (già localizzato). "Titolo - Granborgo".
  usePageTitle(article?.title);

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/api/v1/shop/blog-articles/${slug}?lang=${locale || "it"}`)
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then(setArticle)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug, locale]);

  useLayoutEffect(() => {
    if (loading || error || !article) return;

    const ctx = gsap.context(() => {
      /* ── Title card entrance ── */
      const titleCard = containerRef.current?.querySelector(".gs-blog-title-card");
      if (titleCard) {
        gsap.from(titleCard, {
          y: 50,
          opacity: 0,
          duration: 1.2,
          ease: "expo.out",
          delay: 0.15,
        });
      }

      /* ── Body blocks fade-up on scroll ── */
      gsap.utils.toArray<HTMLElement>(".gs-block").forEach((el) => {
        gsap.from(el, {
          y: 30,
          opacity: 0,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: { trigger: el, start: "top 88%" },
        });
      });

      /* ── Back link fade-in ── */
      gsap.from(".gs-blog-back-link", {
        opacity: 0,
        duration: 0.8,
        ease: "expo.out",
        scrollTrigger: { trigger: ".gs-blog-back-link", start: "top 92%" },
      });
    }, containerRef);

    return () => ctx.revert();
  }, [loading, error, article]);

  if (loading) {
    return (
      <div style={{ padding: "120px 48px", textAlign: "center" }}>
        <div
          style={{
            width: 32,
            height: 32,
            border: `2px solid rgba(28,75,59,0.89)`,
            borderTopColor: cfg.color_primary,
            borderRadius: "50%",
            animation: "spin .8s linear infinite",
            margin: "0 auto",
          }}
        />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ padding: "120px 48px", textAlign: "center" }}>
        <p style={{ color: "rgba(28,75,59,0.86)", marginBottom: 24, fontSize: 16 }}>{t("blog.notFound")}</p>
        <Link
          to={pathFor("blog", locale)}
          style={{
            color: cfg.color_dark,
            fontWeight: 400,
            textDecoration: "none",
            fontSize: 14,
            transition: "opacity .2s",
          }}
        >
          &larr; {t("blog.backToBlog")}
        </Link>
      </div>
    );
  }

  const body = article.body || "";
  const useBlocks = isBlockContent(body);
  let blocks: Block[] = [];
  if (useBlocks) {
    try { blocks = (JSON.parse(body) as Block[]).filter((b) => b.type !== "cta"); } catch { /* fallback */ }
  }

  const hasImage = !!article.image_url;

  const coverImageEl = (
    <img
      src={resolveImageUrl(article.image_url!)}
      alt={article.title}
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        animation: "kenBurns 20s ease-in-out infinite alternate",
      }}
    />
  );

  return (
    <div ref={containerRef}>
      {/* ── Ken Burns keyframes ── */}
      <style>{`
        @keyframes kenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.06); }
        }
      `}</style>

      {/* ── Cover Image (full-width) ── */}
      {hasImage && (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "clamp(280px, 40vh, 480px)",
            overflow: "hidden",
          }}
        >
          {isMobile ? (
            coverImageEl
          ) : (
            <Parallax speed={-4} style={{ width: "100%", height: "100%" }}>
              {coverImageEl}
            </Parallax>
          )}
          {/* Subtle gradient */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,.3) 100%)",
              pointerEvents: "none",
            }}
          />
          {/* Back link on image */}
          <Link
            to={pathFor("blog", locale)}
            style={{
              position: "absolute",
              top: 24,
              left: 24,
              color: "#fff",
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 400,
              background: "rgba(0,0,0,.25)",
              backdropFilter: "blur(8px)",
              padding: "8px 16px",
              borderRadius: 8,
              transition: "opacity .2s",
              zIndex: 3,
            }}
          >
            &larr; {t("blog.backToBlog")}
          </Link>
        </div>
      )}

      {/* ── Title card (overlaps image) ── */}
      <section
        style={{
          background: cfg.color_body_bg,
          padding: hasImage ? "0 24px" : "0 24px",
          paddingTop: hasImage ? 0 : 48,
        }}
      >
        <div
          className="gs-blog-title-card"
          style={{
            maxWidth: 720,
            margin: "0 auto",
            ...(hasImage
              ? {
                  marginTop: isMobile ? -40 : -80,
                  position: "relative" as const,
                  zIndex: 2,
                  background: cfg.color_body_bg,
                  borderRadius: 8,
                  padding: isMobile ? "24px 20px 24px" : "40px 40px 36px",
                  boxShadow: "0 -8px 32px rgba(0,0,0,.06)",
                }
              : {
                  padding: isMobile ? "24px 0 24px" : "40px 0 36px",
                }),
          }}
        >
          {/* Back link (when no image) */}
          {!hasImage && (
            <Link
              to={pathFor("blog", locale)}
              style={{
                color: cfg.color_dark,
                textDecoration: "none",
                fontSize: 13,
                fontWeight: 400,
                display: "inline-block",
                marginBottom: 24,
                transition: "opacity .2s",
              }}
            >
              &larr; {t("blog.backToBlog")}
            </Link>
          )}

          {/* Overline date */}
          <p
            style={{
              fontSize: 11,
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: 2,
              color: cfg.color_muted,
              margin: "0 0 14px",
            }}
          >
            {formatDate(article.created_at, locale || "it")}
          </p>

          {/* Title */}
          <h1
            style={{
              fontFamily: cfg.font_heading,
              fontSize: "clamp(28px, 3.5vw, 36px)",
              fontWeight: 400,
              color: cfg.color_dark,
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            {article.title}
          </h1>

          {/* Excerpt */}
          {article.excerpt && (
            <p
              style={{
                fontSize: 18,
                color: "rgba(28,75,59,0.86)",
                marginTop: 18,
                marginBottom: 0,
                lineHeight: 1.6,
                maxWidth: 720,
              }}
            >
              {article.excerpt}
            </p>
          )}
        </div>
      </section>

      {/* ── Body ── */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "16px 24px 56px" : "24px 48px 96px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          {useBlocks ? (
            <BlockRenderer blocks={blocks} cfg={cfg} />
          ) : (
            <LegacyRenderer body={body} cfg={cfg} />
          )}

          {/* ── Recommended Products/Collections CTA ── */}
          {article.recommended_items && article.recommended_items.length > 0 && (
            <div
              className="gs-block"
              style={{
                marginTop: 64,
                paddingTop: 48,
                borderTop: "1px solid rgba(28,75,59,0.89)",
              }}
            >
              {/* Section header — minimal line + text */}
              <div style={{ marginBottom: 32 }}>
                <div style={{
                  width: 32,
                  height: 1,
                  background: cfg.color_primary,
                  marginBottom: 16,
                }} />
                <h3
                  style={{
                    fontFamily: cfg.font_heading,
                    fontSize: isMobile ? 20 : 24,
                    fontWeight: 400,
                    color: cfg.color_dark,
                    margin: 0,
                    lineHeight: 1.3,
                  }}
                >
                  Potrebbe piacerti
                </h3>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : article.recommended_items.length === 1
                    ? "1fr"
                    : `repeat(${Math.min(article.recommended_items.length, 2)}, 1fr)`,
                  gap: 20,
                }}
              >
                {article.recommended_items.map((item) => {
                  const href =
                    item.type === "product"
                      ? productPath(locale, item.slug)
                      : pathFor("collections", locale);
                  return (
                    <Link
                      key={`${item.type}-${item.slug}`}
                      to={href}
                      style={{
                        display: "block",
                        textDecoration: "none",
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid rgba(28,75,59,0.89)",
                        transition: "border-color 0.25s, box-shadow 0.25s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = cfg.color_dark;
                        e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.08)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "rgba(28,75,59,0.89)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {item.image_url && (
                        <div style={{ overflow: "hidden" }}>
                          <img
                            src={resolveImageUrl(item.image_url)}
                            alt=""
                            style={{
                              width: "100%",
                              height: isMobile ? 180 : 200,
                              objectFit: "cover",
                              display: "block",
                              transition: "transform 0.5s cubic-bezier(0.4,0,0.2,1)",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.04)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                          />
                        </div>
                      )}
                      <div style={{ padding: isMobile ? "16px 16px 20px" : "20px 24px 24px" }}>
                        <p
                          style={{
                            fontSize: 10,
                            fontWeight: 500,
                            letterSpacing: 2,
                            textTransform: "uppercase",
                            color: cfg.color_muted,
                            margin: "0 0 8px",
                          }}
                        >
                          {item.type === "product" ? "Prodotto" : "Collezione"}
                        </p>
                        <p
                          style={{
                            fontSize: 17,
                            fontWeight: 400,
                            color: cfg.color_dark,
                            margin: "0 0 6px",
                            lineHeight: 1.3,
                            fontFamily: cfg.font_heading,
                          }}
                        >
                          {item.title}
                        </p>
                        {item.description && (
                          <p
                            style={{
                              fontSize: 13,
                              color: "rgba(28,75,59,0.86)",
                              margin: "0 0 12px",
                              lineHeight: 1.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {stripHtml(item.description)}
                          </p>
                        )}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}>
                          {item.price_cents != null && (
                            <p
                              style={{
                                fontSize: 15,
                                fontWeight: 500,
                                color: cfg.color_dark,
                                margin: 0,
                              }}
                            >
                              {(item.price_cents / 100).toFixed(2).replace(".", ",")} &euro;
                            </p>
                          )}
                          <span style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: cfg.color_primary,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                          }}>
                            Scopri
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Back to blog */}
          <div className="gs-blog-back-link" style={{ marginTop: 64, paddingTop: 32, borderTop: `1px solid rgba(28,75,59,0.89)` }}>
            <Link
              to={pathFor("blog", locale)}
              style={{
                color: cfg.color_dark,
                fontWeight: 400,
                textDecoration: "none",
                fontSize: 14,
                transition: "opacity .2s",
              }}
            >
              &larr; {t("blog.backToBlog")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
